import { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Loader2, ShieldCheck, ArrowRight, Copy, Check } from "lucide-react";
import { NEXA_APPLICATION_URL } from "@/lib/constants";
import { trackApplyClick } from "@/lib/tracking";
import { getPartnerCode, getCampaign } from "@/lib/attribution";
import { copyText } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApplyIntentContext = createContext(null);

export function useApplyIntent() {
  const ctx = useContext(ApplyIntentContext);
  if (!ctx) throw new Error("useApplyIntent must be used inside <ApplyIntentProvider>");
  return ctx;
}

export function ApplyIntentProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [sourceButton, setSourceButton] = useState("");
  const [target, setTarget] = useState({ applyUrl: NEXA_APPLICATION_URL, mloSlug: null, mloName: "", mloPhone: "" });
  const [full_name, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [consent, setConsent] = useState(false);
  const [issued, setIssued] = useState(null); // { code, apply_url }
  const [copied, setCopied] = useState(false);

  const openIntent = useCallback((source, opts) => {
    setSourceButton(source || "unknown");
    setTarget({
      applyUrl: opts && "applyUrl" in opts ? opts.applyUrl : NEXA_APPLICATION_URL,
      mloSlug: opts?.mloSlug || null,
      mloName: opts?.mloName || "",
      mloPhone: opts?.mloPhone || "",
    });
    setErr("");
    setIssued(null);
    setCopied(false);
    setConsent(false);
    setOpen(true);
  }, []);

  const close = () => {
    setOpen(false);
    // small delay to reset after close animation
    setTimeout(() => {
      setFullName("");
      setEmail("");
      setPhone("");
      setBusy(false);
      setErr("");
      setIssued(null);
      setConsent(false);
      setCopied(false);
    }, 250);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!full_name.trim() || !email.trim() || !phone.trim()) {
      setErr("Please fill in your name, email, and phone.");
      return;
    }
    if (!consent) {
      setErr("Please check the box so we can send your reference code.");
      return;
    }
    setBusy(true);
    setErr("");
    const clickId = trackApplyClick(sourceButton, target.applyUrl || "", target.mloSlug);
    try {
      const { data } = await axios.post(`${API}/apply-intent`, {
        full_name: full_name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        consent: true,
        source_button: sourceButton,
        click_id: clickId,
        mlo_slug: target.mloSlug,
        partner_code: getPartnerCode() || null,
        campaign: getCampaign() || null,
        landing_path: window.location.pathname + window.location.search,
        apply_url: target.applyUrl || null,
      });
      if (target.applyUrl) {
        setIssued({ code: data.code, apply_url: data.apply_url || target.applyUrl });
        setBusy(false);
        return;
      }
      toast.success(
        `Thanks — ${target.mloName ? target.mloName.split(" ")[0] : "your loan officer"} will reach out shortly.`,
      );
      close();
    } catch (e2) {
      // Never trap someone who wants to apply — hand them off untagged.
      // eslint-disable-next-line no-console
      console.warn("apply-intent save failed", e2);
      if (target.applyUrl) {
        window.open(target.applyUrl, "_blank", "noopener,noreferrer");
        close();
      } else {
        setErr("Something went wrong. Please try again.");
        setBusy(false);
      }
    }
  };

  const continueToApplication = () => {
    if (issued?.apply_url) window.open(issued.apply_url, "_blank", "noopener,noreferrer");
    close();
  };

  return (
    <ApplyIntentContext.Provider value={{ openIntent }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            key="apply-intent-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/55 backdrop-blur-sm flex items-center justify-center px-4"
            data-testid="apply-intent-modal"
            onClick={close}
          >
            <motion.form
              key="apply-intent-card"
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={submit}
              className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
            >
              <div className="px-6 pt-6 pb-4 relative">
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  className="absolute top-4 right-4 p-2 rounded-lg hover:bg-[#F4F2EB] text-[#7A7668]"
                  data-testid="apply-intent-close"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] uppercase text-[#1F8F8B]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#A6D24A]" />
                  {issued ? "You're set" : target.applyUrl ? "Before we hand you off" : "Quick intro"}
                </div>
                <h2 className="mt-3 font-heading text-2xl leading-tight text-[#0F2A47]">
                  {issued
                    ? "Here's your reference code."
                    : target.applyUrl
                      ? "Quick intro before we hand you off."
                      : "Quick intro so we can reach you."}
                </h2>
                <p className="mt-2 text-sm text-[#59564C] leading-relaxed">
                  {issued
                    ? `Mention it to ${target.mloName ? target.mloName.split(" ")[0] : "your loan officer"} if they ask how you found them. We've emailed it to you too.`
                    : target.applyUrl
                      ? "Share your details and we'll open your secure NEXA application in a new tab. Takes 10 seconds."
                      : `Share your details and ${target.mloName ? target.mloName.split(" ")[0] : "your loan officer"} will reach out personally to get you started.`}
                </p>
              </div>

              {issued ? (
                <div className="px-6 pb-5">
                  <div className="rounded-2xl bg-[#0F2A47] px-5 py-6 text-center" data-testid="apply-intent-code-card">
                    <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A6D24A]">
                      Reference code
                    </div>
                    <div
                      className="mt-2 text-3xl font-bold tracking-[0.18em] text-white"
                      data-testid="apply-intent-code"
                    >
                      {issued.code}
                    </div>
                    <button
                      type="button"
                      data-testid="apply-intent-code-copy"
                      onClick={async () => {
                        const ok = await copyText(issued.code);
                        setCopied(ok);
                        toast[ok ? "success" : "error"](ok ? "Code copied" : "Copy it manually");
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/70 hover:text-white transition-colors"
                    >
                      {copied ? <Check className="w-3.5 h-3.5 text-[#A6D24A]" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? "Copied" : "Copy code"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-6 pb-4 space-y-3">
                <label className="block">
                  <span className="block text-xs font-medium text-[#0F2A47] uppercase tracking-widest">Full name</span>
                  <input
                    autoFocus
                    type="text"
                    value={full_name}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Jane Homeowner"
                    data-testid="apply-intent-name"
                    className="mt-1 w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-[#0F2A47] uppercase tracking-widest">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="apply-intent-email"
                    className="mt-1 w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-[#0F2A47] uppercase tracking-widest">Phone</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 555-1234"
                    data-testid="apply-intent-phone"
                    className="mt-1 w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white"
                  />
                </label>
                <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="apply-intent-consent"
                    className="mt-0.5 accent-[#1F8F8B]"
                  />
                  <span className="text-[12px] text-[#59564C] leading-relaxed">
                    It's okay to email or text me my reference code and follow up about my application.
                  </span>
                </label>
                {err ? (
                  <p className="text-xs text-red-600" data-testid="apply-intent-error">{err}</p>
                ) : null}
                <p className="text-[11px] text-[#7A7668] flex items-center gap-1.5 pt-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#1F8F8B]" />
                  Your info stays private. No sales calls, no spam.
                </p>
              </div>
              )}

              <div className="px-6 pb-6 pt-2 bg-[#FDFBF7] flex flex-col gap-2">
                {issued ? (
                  <button
                    type="button"
                    onClick={continueToApplication}
                    data-testid="apply-intent-continue"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#A6D24A] hover:bg-[#95c13a] text-[#0F2A47] font-semibold text-sm px-5 py-3 shadow"
                  >
                    Continue to my application
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="apply-intent-submit"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#A6D24A] hover:bg-[#95c13a] text-[#0F2A47] font-semibold text-sm px-5 py-3 shadow disabled:opacity-70"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {target.applyUrl ? "Continue to application" : "Send my details"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                {!issued && !target.applyUrl && target.mloPhone ? (
                  <a
                    href={`tel:${target.mloPhone.replace(/[^+0-9]/g, "")}`}
                    data-testid="apply-intent-call"
                    className="text-xs text-[#7A7668] hover:text-[#0F2A47] underline decoration-[#D9D3BF] underline-offset-4 py-1 text-center"
                  >
                    Or call {target.mloName ? target.mloName.split(" ")[0] : "now"} at {target.mloPhone}
                  </a>
                ) : null}
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </ApplyIntentContext.Provider>
  );
}
